import React from 'react';
import { Box } from '@wix/design-system';
import {
  AppGallery,
  AppOverview,
  AppPageLeanHeader,
  VisibilityDetector,
} from '@wix/app-market-components';
import type { getAppPageDataResponse } from '../app-page-api';
import { useQuickInfoActions } from '../app-page-sections/quick-info/quick-info-actions';
import { AppPermissionsAccordion } from './app-permissions-accordion';
import css from './lean-app-page-info.module.scss';
import {
  useSiteContext,
  useEssentials,
  useRouterContext,
} from '@wix/app-market-core';
import { RoutePath } from '@wix/app-market-core-types';
import { shouldShowPermissionsAndTerms } from '../utils/permissionsAndTerms';
import { dataHooks } from './data-hooks';
import { AppPageType } from '@wix/app-market-core-components-types';

type LeanAppPageInfoProps = Pick<
  getAppPageDataResponse,
  'app' | 'overview' | 'permissions' | 'quickInfo'
> & {
  showNotificationGap?: boolean;
  isSlim?: boolean;
  onShowHeader?: () => void;
  onHideHeader?: () => void;
};

export function LeanAppPageInfo({
  app,
  overview,
  quickInfo,
  onShowHeader,
  onHideHeader,
  permissions = [],
  showNotificationGap = false,
  isSlim = false,
}: LeanAppPageInfoProps) {
  const { languageCode, isPremium } = useSiteContext();
  const { experiments } = useEssentials();

  const { onGalleryChange, onExpandDescriptionButtonClick } =
    useQuickInfoActions(AppPageType.ONE_STEP_SLIM);
  const shouldShowPermissions = shouldShowPermissionsAndTerms(
    permissions ?? [],
    app.installation.isInstalled,
  );

  const getContainerMaxHeight = () => {
    if (shouldShowPermissions) {
      return showNotificationGap ? '323px' : '425px';
    }
    return showNotificationGap ? '406px' : '481px';
  };

  const { router } = useRouterContext();

  const handleLearnMoreClick = () => {
    router.navigateTo({
      path: RoutePath.APP_PAGE,
      payload: { slug: app.slug },
    });
  };
  return (
    <Box
      className={css.container}
      dataHook={dataHooks.leanAppPageInfo}
      maxHeight={getContainerMaxHeight()}
    >
      <Box
        direction="vertical"
        gap="SP4"
        className={css.appInfo}
        dataHook={dataHooks.leanAppPageInfoAppInfo}
      >
        {isSlim && (
          <Box dataHook={dataHooks.leanAppPageInfoLeanHeaderContainer}>
            <VisibilityDetector
              onHidden={onShowHeader}
              onVisible={onHideHeader}
            >
              <AppPageLeanHeader
                image={app.icon}
                appData={app}
                isPremiumSite={isPremium}
                languageCode={languageCode}
                experiments={experiments}
                isNewSlim={isSlim}
              />
            </VisibilityDetector>
          </Box>
        )}
        <AppOverview
          appName={app.name}
          description={!isSlim ? undefined : overview.description}
          benefits={overview.benefits}
          onExpandToggleClick={(isExpanded: boolean) =>
            onExpandDescriptionButtonClick?.(app.id, isExpanded)
          }
          rows={shouldShowPermissions ? 8 : 0}
          collapsible={shouldShowPermissions}
          showHeading={!isSlim}
          size="small"
          onLearnMoreClick={!isSlim ? handleLearnMoreClick : undefined}
          languageCode={languageCode}
          showAdaptiveLayout={true}
        />

        {shouldShowPermissions && (
          <AppPermissionsAccordion
            appId={app.id}
            appName={app.name}
            permissions={permissions}
            appPageType={AppPageType.ONE_STEP_SLIM}
          />
        )}
      </Box>

      <div
        className={css.appGallery}
        data-hook={dataHooks.leanAppPageInfoAppGallery}
      >
        <AppGallery
          media={quickInfo.media}
          onGalleryChange={(oldIndex, newIndex) =>
            onGalleryChange(oldIndex, newIndex, app.id)
          }
        />
      </div>
    </Box>
  );
}
